import { forkJoin } from "rxjs";
import { AdminsApi, AdminSummary, IdentityProviderModel, TenantIdentityProviderSummary } from "../../hickory-api";
import { ApiConfig } from "../../shared/api-config/api-config";
import { BaseDataSource, TableColumn } from "../../shared/data-table/datasource/datasource";
import { IdentityProvidersHelperService } from "../identity-providers-helper.service";
import { IdentityProviderTableContext } from './identity-provider-table-context';
import { computed, signal } from "@angular/core";
import { IdpModelAdminFilter, IdpModelWithSummary } from "./IdpModelWithSummary";
import { AdminsTableColumns, ListAdminsRequest } from "src/app/shared/data-table/datasource/admins-datasource";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";

export abstract class IdentityProvidersDataSource extends BaseDataSource<IdentityProviderModel, IdentityProvidersHelperService, { tenantId?: number }> {
  protected columns: TableColumn<IdentityProviderModel>[] = [
    {
      field: 'name',
      localizationKey: 'IdentityProviders.Columns.Name',
      render(model: IdentityProviderModel) {
        return model?.name
      },
      noSort: true
    },
    {
      field: 'scheme',
      localizationKey: 'IdentityProviders.Columns.Scheme',
      render(model: IdentityProviderModel) {
        return model?.scheme
      },
      noSort: true
    },
  ];

  protected summary = signal<TenantIdentityProviderSummary[]>([]);

  get availableColumns(): TableColumn<IdentityProviderModel>[] {
    return this.columns;
  }
  get queryArguments(): object {
    const request = this.currentRequest.value;
    return {
      tenantId: request?.tenantId ?? +ApiConfig.tenantId
    }
  }
}

export class SystemIdentityProvidersDataSource extends IdentityProvidersDataSource {

  constructor(service: IdentityProvidersHelperService) {
    super(service, null, null, {}, false);
  }

  public loadData(_: boolean): void {
    this.service.getSystemProviders().subscribe(providers => {
      this.dataSubject.next(providers || []);
      this.loadingSubject.next(false);
    });
  }
}

export class TenantIdentityProvidersDataSource extends IdentityProvidersDataSource {

  override get availableColumns(): TableColumn<IdpModelWithSummary>[] {
    return [
      ...this.columns,
      {
        field: 'adminCount',
        localizationKey: 'IdentityProviders.Columns.AdminCount',
        render(model: IdpModelWithSummary) {
          return model?.adminCount ?? 0;
        },
        noSort: true
      },
      {
        field: 'contactCount',
        localizationKey: 'IdentityProviders.Columns.ContactCount',
        render(model: IdpModelWithSummary) {
          return model?.contactCount ?? 0;
        },
        noSort: true
      },
      {
        field: 'uniqueUserCount',
        localizationKey: 'IdentityProviders.Columns.UserCount',
        render(model: IdpModelWithSummary) {
          return model?.uniqueUserCount ?? 0;
        },
        noSort: true
      }
    ]
  }

  private readonly featureEnabled: boolean = false;
  private readonly tenantId: number = null;

  constructor(service: IdentityProvidersHelperService, context: IdentityProviderTableContext) {
    const tenantId = context?.tenantId ?? +ApiConfig.tenantId;
    super(service, null, null, { tenantId }, false);
    this.featureEnabled = context?.featureEnabled;
    this.tenantId = tenantId;
  }

  public loadData(force: boolean): void {
    if(!force) return;
    this.loadingSubject.next(true);
    if (this.featureEnabled) {
      forkJoin([this.service.getTenantIdpSummary(this.tenantId), this.service.tenantProviders(this.tenantId)]).subscribe(([summaries, providers]) => {
        let data: IdpModelWithSummary[] = providers || [];
        if (summaries?.length > 0) {
          this.summary.set(summaries);
          const summaryMap = new Map(summaries.map(s => [s.providerId ?? 'no_provider', s]));
          data = providers.map(p => {
            const summary = summaryMap.get(p.id);
            return {
              ...p,
              ...summary,
              adminFilter: {
                identityProvider: p.scheme,
                onlyIncludeAdminsWithoutIdp: false
              }
            };
          });

          data.push(TenantIdentityProvidersDataSource.GetPlaceHolderProvider(summaryMap.get('no_provider')));
        }

        this.dataSubject.next(data || []);
        this.itemCount = data?.length ?? 0;
        this.loadingSubject.next(false);
        // You can use the summaries here as needed
      });
    } else {
      this.dataSubject.next([]);
      this.loadingSubject.next(false);
    }
  }

  public static GetPlaceHolderProvider(noProviderSummary: Partial<TenantIdentityProviderSummary>): IdpModelWithSummary {
    return {
      name: 'Admins with No Provider',
      uniqueUserCount: noProviderSummary?.uniqueUserCount ?? 0,
      adminCount: noProviderSummary?.adminCount ?? 0,
      contactCount: noProviderSummary?.contactCount ?? 0,
      adminFilter: {
        identityProvider: null,
        onlyIncludeAdminsWithoutIdp: true
      },
      isPlaceholderProvider: true
    }
  }
}

export class IdpAdminsDatasource extends BaseDataSource<AdminSummary, AdminsApi, ListAdminsRequest> {

  private readonly _pageSize = computed(() => this._request().pageSize ?? 10);
  private readonly _page = computed(() => this._request().page ?? 1);
  private readonly _tenantId = signal<number>(+ApiConfig.tenantId);
  private readonly _requestConsts = computed<Partial<ListAdminsRequest>>(() => ({
    tenantId: this._tenantId(),
    page: this._page(),
    pageSize: this._pageSize()
  }));
  private readonly _request = signal<ListAdminsRequest>(null);


  private readonly allColumns = AdminsTableColumns.columns
    .filter(x => x.field !== 'lastName, firstName' && x.field !== 'email')
    .map(x => ({ ...x, hideByDefault: true }));

  protected columns = [
    {
      field: 'lastName, firstName',
      localizationKey: 'Admins.Table.Columns.Name',
      style: { 'min-width': '15rem', padding: '0 0.5rem' },
      render: (admin: AdminSummary) => {
        return `${admin.lastName}, ${admin.firstName}`;
      },
      link: (admin: AdminSummary, queryParams = null, _ = null) => {
        return {
          route: ['/admins', admin.id],
          queryParams: queryParams,
        };
      },
      sticky: true,
    },
    {
      field: 'email',
      localizationKey: 'Admins.Table.Columns.Email',
      filterable: true,
      style: { 'min-width': '25rem' },
    },
    ...this.allColumns
  ];

  get availableColumns(): TableColumn<AdminSummary>[] {
    return this.columns;
  }

  get queryArguments(): ListAdminsRequest {
    const request = this._request();
    return {
      tenantId: request?.tenantId ?? +ApiConfig.tenantId,
      page: request?.page ?? 1,
      pageSize: request?.pageSize ?? 10,
      ...request
    };
  }

  constructor(
    service: AdminsApi,
    tenantId: number,
    paginator: MatPaginator,
    sort: MatSort,
    filter: IdpModelAdminFilter) {
    const tenantIdToUse = tenantId ?? +ApiConfig.tenantId;
    const request = {
      tenantId: tenantIdToUse,
      page: 1,
      pageSize: 10,
      ...filter
    };
    super(service, paginator, sort, request, true);
    this._tenantId.set(tenantId ?? +ApiConfig.tenantId);
    this._request.set(request);
  }

  public loadData(_: boolean): void {
    /** noop use load instead **/
  }

  public load(filter: Partial<ListAdminsRequest>) {
    this.dataSubject.next([]);
    this.itemCount = 0;

    const request = {
      ...this._request(),
      ...this._requestConsts(),
      ...filter
    }

    this._request.set(request);

    this.service.getAdminUsers(
      request.page,                         //page
      request.pageSize,                     //pageSize
      request.sort,                         //sort
      null,                                 //ids
      request.name,                                 //name
      null,                                 //username
      null,                                 //email
      null,                                 //group
      null,                                 //role
      request.tenantId,                     //tenantId
      null,                                 //tenantName
      null,                                 //state
      null,                                 //credentialIds
      null,                                 //externalIds
      request.identityProvider,             //identityProvider
      request.onlyIncludeAdminsWithoutIdp   //onlyIncludeAdminsWithoutIdp
    ).subscribe({
      next: response => {
        this.itemCount = response?.totalItemCount ?? 0;
        this.dataSubject.next(response?.items || []);
      },
    });
  }
} 